/**
 * Tournament Card Component (Campaign Variant)
 * Displays tournament info inside the campaign card system with collapse/expand support.
 * Follows the same pattern as PrizePopCard but uses tournament-specific resources.
 */

import React, { useMemo } from 'react';
import {
  type ICampaignInfo,
  type ITournamentResources,
  CAMPAIGN_INFO_TYPES,
  TOURNAMENTS_CAMPAIGN_TYPE,
  normalizeCampaignAssets,
  useCampaignCardScale,
  useCampaignState,
  useCampaignDataAttributes,
  useCampaignCardStyles,
} from 'features/campaigns';
import PrizeFundCampaign from '../../../shared/PrizeFundCampaign/PrizeFundCampaign';
import BottomCardInfoCampaign from '../../../shared/BottomCardInfo/BottomCardrInfoCampaign';
import CampaignInfoDisplay from '../../../shared/BottomCardInfo/components/CampaignInfoDisplay';
import Image from 'shared/components/Image';
import { useTrueMobile } from 'shared/hooks/useTrueMobile';
import originalHasManyStyles from '../../../shared/CampaignCard/hasManyStyles.module.css';
import type { CampaignCardStyles } from '../../../shared/CampaignCard/CampaignCardStyles';
import { getCollapsedCycleType } from '../../../../utils/campaignHelpers';

type TournamentCardProps = Omit<ICampaignInfo, 'component'> & {
  resources: ITournamentResources;
};

const TournamentCard: React.FC<TournamentCardProps> = ({ name, resources, prizeFund, baseCurrency, campaignId }) => {
  const { isCollapsed, collapsedAttributes, getCollapsedClassName } = useCampaignState();
  const { getSharedCardStyles } = useCampaignCardStyles();
  const collapsedCycleType = getCollapsedCycleType(TOURNAMENTS_CAMPAIGN_TYPE);
  const dataAttributes = useCampaignDataAttributes({
    additionalProps: { 'data-variant': 'card' }
  });
  const hasMany = dataAttributes['data-has-many'];
  const isMobile = useTrueMobile();

  const sharedCardStyles = getSharedCardStyles();
  const hasManyStyles = originalHasManyStyles as unknown as CampaignCardStyles;

  const getMaxWidth = () => {
    if (!isMobile) return isCollapsed ? 370 : 445;
    if (isCollapsed) return 280;
    return hasMany ? 320 : 350;
  };

  const { titleRef, scale } = useCampaignCardScale(name, {
    maxWidth: getMaxWidth(),
    recalculateKey: isCollapsed,
  });

  const normalizedAssets = useMemo(
    () => normalizeCampaignAssets(resources, TOURNAMENTS_CAMPAIGN_TYPE),
    [resources],
  );

  return (
    <div
      className={`${sharedCardStyles.campaignCardContainer} ${hasMany ? hasManyStyles.campaignCardContainer : ''}`}
      {...collapsedAttributes}
      {...dataAttributes}
    >
      <div
        className={`${sharedCardStyles.topHeaderContainerCollapsed} ${hasMany ? hasManyStyles.topHeaderContainerCollapsed : ''}`}
        {...dataAttributes}
      >
        {normalizedAssets.icons.logo && (
          <Image
            src={normalizedAssets.icons.logo}
            className={`${sharedCardStyles.campaignIcon} ${sharedCardStyles.collapsedOnly}`}
          />
        )}

        <div
          className={`${sharedCardStyles.namePrizeWrapper} ${hasMany ? hasManyStyles.namePrizeWrapper : ''}`}
          {...dataAttributes}
        >
          <div
            className={`${getCollapsedClassName(
              sharedCardStyles.campaignTitleContainer,
              sharedCardStyles.campaignTitleCollapsed,
            )} ${hasMany ? hasManyStyles.campaignTitleContainer : ''}`}
            {...(isCollapsed ? collapsedAttributes : {})}
            {...dataAttributes}
          >
            <span
              className={sharedCardStyles.campaignNameText}
              ref={titleRef as React.RefObject<HTMLSpanElement>}
              style={{
                transform: scale < 1 ? `scale(${scale})` : undefined,
              }}
            >
              {name}
            </span>
            {isCollapsed && collapsedCycleType && (
              <CampaignInfoDisplay infoTypes={[collapsedCycleType]} campaignId={campaignId} />
            )}
          </div>
          <PrizeFundCampaign amount={prizeFund} currency={baseCurrency} variant="card" />
        </div>

        <div className={sharedCardStyles.collapsedOnly}>
          <CampaignInfoDisplay infoTypes={[CAMPAIGN_INFO_TYPES.TIMER]} campaignId={campaignId} />
        </div>
      </div>

      <div
        className={`${sharedCardStyles.separatorAndInfoContainer} ${sharedCardStyles.expandedOnly}`}
        {...dataAttributes}
      >
        <div
          className={`${sharedCardStyles.separator} ${hasMany ? hasManyStyles.separator : ''}`}
          {...dataAttributes}
        />
        <BottomCardInfoCampaign campaignId={campaignId} />
      </div>
    </div>
  );
};

export default TournamentCard;
